import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { registerRouteTitle } from "@/lib/analytics";
import { useLocale } from "@/lib/i18n/useLocale";
import { localisePath, HTML_LANG } from "@/lib/i18n/registry";

const SITE_NAME = "Event Boutique";
const DEFAULT_OG_IMAGE = "https://eventboutique.pt/img/eb-og-default.webp";
const BASE_URL = "https://eventboutique.pt";

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogImageAlt?: string;
  noIndex?: boolean;
  schemaJson?: Record<string, unknown> | Record<string, unknown>[];
  /**
   * Код языка для <html lang> (BTQ-47, Этап 1). Португальские маршруты
   * передают "pt-PT". Атрибут ставится и воркером — на сыром HTML, до
   * гидратации; здесь он нужен для навигации внутри SPA, где воркер
   * уже не участвует, а язык страницы меняется.
   */
  lang?: string;
}

const SEOHead = ({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogImageAlt = "A wedding produced by Event Boutique in Portugal",
  noIndex = false,
  schemaJson,
  lang,
}: SEOHeadProps) => {
  // Hand the page's own title to the measurement layer (BTQ-65). GA4 needs the
  // title of the page being navigated TO, and document.title is not it: on
  // client-side navigation this app never rewrites the head, so anything read
  // from the DOM after a route change is the previous page's title.
  useEffect(() => {
    registerRouteTitle(title);
  }, [title]);

  // Канонический адрес и <html lang> берутся из локали текущего пути, а не из
  // литерала на странице. До этого португальская страница площадки объявляла
  // себя английской и канонизировалась на английский адрес: страницы прописаны
  // литералами в 28 файлах, и каждый пришлось бы править отдельно (BTQ-47).
  const pageLocale = useLocale();
  const localised = canonical && !canonical.startsWith("http")
    ? localisePath(canonical, pageLocale) ?? canonical
    : canonical;
  const fullCanonical = localised
    ? localised.startsWith("http") ? localised : `${BASE_URL}${localised}`
    : undefined;
  const htmlLang = lang ?? HTML_LANG[pageLocale];

  return (
    <Helmet htmlAttributes={{ lang: htmlLang }}>
      <title>{title}</title>
      <meta name="description" content={description} />
      {fullCanonical && <link rel="canonical" href={fullCanonical} />}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={ogImageAlt} />
      {fullCanonical && <meta property="og:url" content={fullCanonical} />}
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Schema JSON-LD */}
      {schemaJson && (
        <script type="application/ld+json">
          {JSON.stringify(schemaJson)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
